import { AppDispatch } from '@/app/store';
import { MESSAGE_STAGED_PHOTOS_EXPIRED } from '@/utils/validation-utils';
import { STAGED_PHOTO_TIME_TO_LIVE_MS, StagedPhotoDTO, isStagedPhotoExpired } from '@/types/create/StagedPhotoDTO';
import { getStagedPhotos, setStagedPhotos } from '@/services/createFormSlice';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useEffect } from 'react';
import { useGetClientInfoQuery } from '@/services/api/createFormApiSlice';
import { useSnackbar } from 'notistack';

/**
 * Drops the staged photos the server has most likely swept already, and tells the sender they have
 * to be attached again.
 * <p>
 * The create form is persisted indefinitely while the staging rows behind it live for hours, so a
 * sender who comes back the next day would otherwise see their thumbnails, be quoted for them, and
 * be refused at creation with nothing on screen explaining why. Kept in one hook because every step
 * of the create form that shows or prices staged photos has to agree on what is still there,
 * and because that agreement now includes reading the period the server is actually sweeping on.
 *
 * @param enabled Whether the check applies. Pass `false` once the letter has been created: the
 *                photos are then part of a receipt rather than of a form, and clearing them would
 *                rewrite what the sender has already paid for.
 */
export const useStagedPhotoExpiry = (enabled = true): void => {
  const dispatch: AppDispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const stagedPhotos: StagedPhotoDTO[] = useAppSelector(getStagedPhotos);
  const { data: clientInfo, isLoading, isUninitialized } = useGetClientInfoQuery();
  const timeToLiveMs: number = clientInfo?.stagedPhotoTimeToLiveMs ?? STAGED_PHOTO_TIME_TO_LIVE_MS;
  const unanswered: boolean = isUninitialized || isLoading;

  useEffect((): void => {
    const isExpired = (photo: StagedPhotoDTO): boolean => isStagedPhotoExpired(photo, timeToLiveMs);
    if (!enabled || unanswered || !stagedPhotos.some(isExpired)) {
      return;
    }
    dispatch(setStagedPhotos(stagedPhotos.filter((photo: StagedPhotoDTO): boolean => !isExpired(photo))));
    enqueueSnackbar(MESSAGE_STAGED_PHOTOS_EXPIRED, {
      variant: 'warning',
      preventDuplicate: true,
    });
  }, [dispatch, enabled, enqueueSnackbar, stagedPhotos, timeToLiveMs, unanswered]);
};
